import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import {
  listBreeding,
  startBreeding,
  claimBreeding,
  cancelBreeding,
  listEggs,
  startEggIncubation,
  listBreedingMoves,
} from "@/lib/game/breeding.functions";
import { hatchEgg, regenerateMonsterAppearance, listMyMonsters } from "@/lib/game/monster.functions";
import { sellItem, sellPrice, sellAllEggs } from "@/lib/game/shop.functions";
// Ancestry-Imports entfernt (Stufe 2). Feste Werte statt Perks.
const INCUBATOR_CAP = 2;
import {
  rarityFromLevel,
  MOVES_PER_RARITY,
  EGG_RARITY_STAT_BONUS,
  type EggRarity,
} from "@/lib/game/eggs";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { ArrowRight, CheckCircle2, Coins, Egg, Hourglass, Sparkles, Swords, X } from "lucide-react";
import { toast } from "sonner";
import { CREATURE_TYPES, typeLabel, type CreatureType } from "@/lib/game/types";

import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { InfoTip } from "@/components/game/InfoTip";
import { Panel } from "@/components/game/Panel";
import { PortraitFrame } from "@/components/game/PortraitFrame";
import { Pill, RarityTag, TypeBadge, type Rarity } from "@/components/game/Badges";
import {
  FilterPills,
  MicroLabel,
  PageHeader,
  ScreenContainer,
} from "@/components/game/ScreenKit";
import { useTutorial } from "@/components/game/TutorialProvider";
import { useTranslation } from "react-i18next";
import { currentLang } from "@/lib/i18n/lang";
import { tServerError } from "@/lib/i18n/server-error";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/game/breeding")({
  component: Breeding,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

type Parent = {
  id: string;
  name: string;
  type: string;
  level: number;
  image_url: string | null;
};

type Job = {
  id: string;
  parent_a_name: string;
  parent_b_name: string;
  parent_a_type: string;
  parent_b_type: string;
  result_type: string;
  stat_bonus_pct: number;
  inherited_move_ids: string[];
  started_at: string;
  ready_at: string;
  status: string;
};

/* ────────────────────────────────────────────────────────────────────────────
 * Erklär- und Fließtexte dieses Screens — IMMER zweisprachig
 *
 * Die Erklär-Blasen standen bis 05.09.2026 in `lib/game/tooltips.ts`
 * (BREEDING_TIPS) und beschrieben eine Mechanik, die es so nicht mehr gibt:
 *  - „1–2 Attacken eines Elternteils werden zufällig vererbt" — der Operator
 *    wählt die Attacken seit `listBreedingMoves`/`startBreeding` selbst aus,
 *    und die Ei-Rarität deckelt die Anzahl (MOVES_PER_RARITY).
 *  - „Während der Brut kannst du das Ei nicht verkaufen" — `sellItem` prüft
 *    `hatch_at` überhaupt nicht, der Verkauf läuft auch während der Brut.
 * Deshalb stehen sie jetzt hier, direkt neben dem Code, den sie beschreiben.
 *
 * ⚠️ `BREEDING_TIPS` lieferte über `currentLang()` DE **und** EN. Wer den Text
 * hierher holt, muss beide Sprachen mitbringen — sonst sieht ein englischer
 * Spieler auf einem sonst übersetzten Screen deutsche Blöcke. Das gilt auch
 * für den neuen Fließtext (`TXT` weiter unten).
 * 📌 Zwischenlösung: Diese Sätze gehören als `breeding.*`-Keys nach
 * `src/i18n/locales/{de,en}/game.json` und über `t()` gelesen — das ist eine
 * Änderung an gemeinsamen Dateien und steht als offener Punkt im Ergebnis.
 * ──────────────────────────────────────────────────────────────────────────── */
type Bi = { de: string; en: string };
/** Liest die passende Sprachfassung. Aufruf im Render — die Komponenten hängen
 *  an `useTranslation` und rendern beim Sprachwechsel neu. */
const bi = (b: Bi): string => (currentLang() === "en" ? b.en : b.de);

const TIP_INHERITANCE: Bi = {
  de: "Du wählst beim Start selbst aus, welche Eltern-Attacken vererbt werden. Wie viele davon ins Ei passen, hängt von seiner Rarität ab — deine Reihenfolge entscheidet, welche es sind.",
  en: "You pick yourself which parent moves are passed on. How many of them fit into the egg depends on its rarity — your order decides which ones make it.",
};
const TIP_HATCH_TIME: Bi = {
  de: "Ein Ei brütet rund 2–5 Minuten, seltenere etwas länger. Höchstens 2 Eier brüten gleichzeitig. Verkaufen kannst du ein Ei auch währenddessen.",
  en: "An egg incubates for about 2–5 minutes, rarer ones a little longer. At most 2 eggs incubate at the same time. You can sell an egg while it is incubating.",
};
const TIP_INCUBATOR: Bi = {
  de: "Bricht die Reifung ab: Die Eltern sind sofort wieder frei, das Ei ist verloren.",
  en: "Cancels the ripening: the parents are free again immediately, the egg is lost.",
};
const TIP_SLOTS: Bi = {
  de: "So viele Eier reifen gleichzeitig im Nest. Ist alles belegt, brüte erst eines aus oder verkaufe es.",
  en: "That many eggs incubate in the nest at the same time. If every slot is taken, hatch one first or sell it.",
};
const TIP_RIPE_TIME: Bi = {
  de: "Je höher die Level der beiden Eltern, desto kürzer die Reifezeit.",
  en: "The higher the levels of both parents, the shorter the ripening time.",
};
const TIP_STAT_BONUS: Bi = {
  de: "Aufschlag auf die Startwerte des Schlüpflings (HP, ATK, DEF). Er hängt allein an der Rarität des Eis.",
  en: "Bonus on the hatchling's starting values (HP, ATK, DEF). It depends solely on the egg's rarity.",
};
const TIP_EGG_TYPE_OPEN: Bi = {
  de: "Dieses Ei trägt keinen festen Typ — beim Ausbrüten wird einer ausgewürfelt.",
  en: "This egg carries no fixed type — one is rolled when it hatches.",
};

/** Fließtext des Screens (Wege ins Nest, Vorschau, Kacheln). */
const TXT = {
  wayBreedLabel: { de: "Weg 1 · Zucht", en: "Path 1 · Breeding" },
  wayBreedBody: {
    de: "Zwei Xenomons ab Lv {{lvl}}, die gerade nichts anderes tun, hinterlassen nach einer Reifezeit ein Brut-Ei im Nest. Nur so werden Eltern-Attacken vererbt.",
    en: "Two Xenomons of Lv {{lvl}} or higher that are not busy leave a bred egg in the nest after a ripening time. This is the only way parent moves are passed on.",
  },
  wayBattleLabel: { de: "Weg 2 · Kampf", en: "Path 2 · Combat" },
  wayBattleBody: {
    de: "Bosse und Elite-Gegner lassen in späteren Zonen bei der Erstbegehung einer Stage Eier fallen. Die landen ohne Umweg im Eier-Nest — ohne geerbte Attacken.",
    en: "In later zones, bosses and elite enemies drop eggs on the first clear of a stage. Those go straight into the egg nest — without inherited moves.",
  },
  nestNote: {
    de: "Jedes Ei muss danach im Nest erst die Brutzeit absitzen:",
    en: "Every egg then has to sit out its incubation time in the nest:",
  },
  previewTitle: { de: "Vorschau", en: "Preview" },
  previewTypeRoll: {
    de: "Der Typ des Eis wird beim Start ausgewürfelt — 50 / 50 zwischen beiden Eltern.",
    en: "The egg's type is rolled at the start — 50 / 50 between both parents.",
  },
  previewPickHint: {
    de: "Wähle links zwei Xenomons aus.",
    en: "Pick two Xenomons on the left.",
  },
  /**
   * Die Obergrenze steht als `{{n}}` drin und wird beim Rendern aus
   * `HATCH_INHERIT_LIMIT` gefüllt — derselben Konstante, mit der die Vorschau
   * rechnet (Zeile ~612). Eine zweite, hier hingeschriebene Zahl würde beim
   * nächsten Anfassen der Konstante still auseinanderlaufen.
   *
   * ⚠️ Der i18n-Key `breeding.subtitleMoves` in
   * `src/i18n/locales/{de,en}/game.json` sagt weiter „1–4" und wird von KEINER
   * Zeile mehr gelesen (nur `subtitleMovesSuffix` läuft noch über `t()`). Wer
   * ihn dort korrigiert, ändert an der Anzeige nichts. Er gehört auf „1–3"
   * gezogen oder entfernt — fremde Datei, deshalb hier nur der Hinweis.
   */
  subtitleMoves: { de: "1–{{n}} Eltern-Attacken", en: "1–{{n}} parent moves" },
  statBonus: { de: "Stat-Bonus", en: "Stat bonus" },
  ripeTime: { de: "Reifezeit", en: "Ripening time" },
  minutesShort: { de: "Min", en: "min" },
  cancelQuestion: { de: "Abbrechen?", en: "Cancel?" },
  typeOpen: { de: "Typ offen", en: "Type open" },
  statsSuffix: { de: "% Stats", en: "% stats" },
  inNest: { de: "im Nest", en: "in the nest" },
};

/** Die vier Raritäten, die Eier überhaupt tragen können (`EggRarity`). */
const RARITY_LABELS: Record<string, Bi> = {
  common: { de: "Gewöhnlich", en: "Common" },
  rare: { de: "Selten", en: "Rare" },
  epic: { de: "Episch", en: "Epic" },
  legendary: { de: "Legendär", en: "Legendary" },
};
const isRarity = (r: unknown): r is Rarity =>
  typeof r === "string" && r in RARITY_LABELS;

/**
 * ⚠️ So viele geerbte Attacken kommen beim Schlüpfen wirklich an.
 *
 * `hatchEgg` (src/lib/game/monster.functions.ts) trägt
 * `inherited_move_ids.slice(0, 3)` in `monster_moves` ein — die vierte,
 * bewusst ausgewählte Attacke wird kommentarlos verworfen.
 * `MOVES_PER_RARITY.legendary` steht dagegen auf 4, und `startBreeding`
 * speichert auch vier. Bis geklärt ist, welche Zahl gilt (Backlog), deckelt
 * die Anzeige hier auf den Wert, den der Spieler tatsächlich bekommt —
 * lieber eine Attacke zu wenig versprechen als eine zu viel.
 */
const HATCH_INHERIT_LIMIT = 3;
const isCreatureType = (t: unknown): t is CreatureType =>
  typeof t === "string" && (CREATURE_TYPES as readonly string[]).includes(t);

/**
 * Filter-Pille für die Mehrfachauswahl (Typ, Rarität, Status).
 *
 * Die Einfachauswahl („Sortieren") läuft über `FilterPills` aus dem ScreenKit;
 * das kann nur EINEN Wert. Hier ist die Auswahl ein Set, deshalb eine eigene
 * Pille — die Höhenstaffelung ist aber dieselbe wie im Baustein
 * (`min-h-11 sm:min-h-9`): In der Eltern-Auswahl und im Eier-Nest stehen beide
 * Reihen direkt untereinander, und 44 px auf dem Handy sind Vorgabe. Wer hier
 * eine Höhe setzt, ändert sie in `ScreenKit.tsx` mit — `min-h-9` allein würde
 * über tailwind-merge das `min-h-11` der Button-Variante verdrängen.
 */
function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? "default" : "secondary"}
      onClick={onClick}
      className="min-h-11 px-2.5 font-display text-[10px] font-normal uppercase tracking-[0.1em] sm:min-h-9"
    >
      {children}
    </Button>
  );
}

/**
 * Ei-Kachel: cremefarbenes Fenster wie das Art-Fenster der Kreaturen, das Ei
 * selbst in der Typfarbe. Kennt das Ei keinen Typ, bleibt es in Ink — der
 * Schlüpfling würfelt seinen Typ dann beim Ausbrüten aus (`hatchEgg` →
 * `randomType()`), und eine Typfarbe hier wäre schlicht geraten.
 */
function EggTile({ type, size = 52 }: { type: CreatureType | null; size?: number }) {
  return (
    <div
      className="grid shrink-0 place-items-center rounded-md border-2 border-ink bg-cream"
      style={{ width: size, height: size }}
    >
      <Egg
        strokeWidth={2.5}
        style={{
          width: Math.round(size * 0.5),
          height: Math.round(size * 0.5),
          color: type ? `var(--type-${type})` : "var(--ink)",
        }}
      />
    </div>
  );
}

/**
 * Zeitbalken für Reifung und Brut. Bewusst nicht `StatBar`: die zeigt neben
 * dem Label immer „Wert/Maximum" — bei Millisekunden also Zahlensalat.
 * Farbe ist `--accent` (Art-Blau = Info/Cooldown), nicht Gold: Gold gehört im
 * Design der Gold-Ökonomie.
 */
function TimeTrack({ pct }: { pct: number }) {
  return (
    <div className="relative box-border h-3.5 overflow-hidden rounded-full border-2 border-ink bg-ink">
      <div
        className="h-full rounded-full"
        style={{
          width: `${pct}%`,
          background: "linear-gradient(180deg, oklch(0.72 0.14 262) 0 45%, var(--accent) 45%)",
          transition: "width .35s cubic-bezier(.34,1.3,.64,1)",
        }}
      />
    </div>
  );
}

/** Kleine Zusatz-Pille (Herkunft, Stat-Bonus, geerbte Attacken). */
function InfoPill({ children }: { children: React.ReactNode }) {
  return (
    <Pill style={{ background: "var(--secondary)", color: "var(--foreground)" }}>{children}</Pill>
  );
}

/** Beschriftete Zeile in der Vorschau: Label links, Wert rechts. */
function PreviewRow({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5 font-extrabold tabular-nums">{value}</span>
    </div>
  );
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "✓";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function Breeding() {
  // ⚠️ `steps.breeding_intro.body` (src/i18n/locales/{de,en}/tutorial.json)
  // behauptet vererbte Eltern-Stats und -Eigenschaften. `hatchEgg` würfelt
  // Werte, Aspekt und Wesenszug neu (STARTER_STATS × Raritäts-Bonus,
  // rollGeneMultipliers, rollAspect, pickRandomTrait) — vererbt werden allein
  // die gewählten Attacken-IDs. Der Satz gehört in der JSON korrigiert
  // (§6j(D): die JSON gewinnt, tutorial-steps.ts allein zu ändern wirkt nicht);
  // das ist eine gemeinsame Datei und steht als offener Punkt im Ergebnis.
  useTutorial("breeding_intro");
  useTutorial("hatch_coach");
  const { t } = useTranslation("game");
  const qc = useQueryClient();
  const list = useServerFn(listBreeding);
  const start = useServerFn(startBreeding);
  const claim = useServerFn(claimBreeding);
  const cancel = useServerFn(cancelBreeding);
  const fetchEggs = useServerFn(listEggs);
  const hatch = useServerFn(hatchEgg);
  const startIncubate = useServerFn(startEggIncubation);
  const sellFn = useServerFn(sellItem);
  const sellAllEggsFn = useServerFn(sellAllEggs);
  const regen = useServerFn(regenerateMonsterAppearance);
  const fetchBreedingMoves = useServerFn(listBreedingMoves);

  const { data, isLoading } = useQuery({
    queryKey: ["breeding"],
    queryFn: () => list(),
    refetchInterval: 60_000,
  });

  const { data: eggData } = useQuery({
    queryKey: ["eggs"],
    queryFn: () => fetchEggs(),
    refetchInterval: 60_000,
  });

  const fetchMyMonsters = useServerFn(listMyMonsters);
  const { data: monstersData } = useQuery({
    queryKey: ["my-monsters"],
    queryFn: () => fetchMyMonsters(),
  });
  const teamCount = monstersData?.monsters?.length ?? 0;
  const teamCap = monstersData?.cap ?? 6;
  const teamFull = teamCount >= teamCap;

  const incubatorCap = INCUBATOR_CAP;
  const minLevel = data?.requirements.minLevel ?? 15;
  const baseDurationMinutes = data?.requirements.baseDurationSeconds
    ? Math.round(data.requirements.baseDurationSeconds / 60)
    : null;

  // Ticker für countdown
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, []);

  const [pickA, setPickA] = useState<string | null>(null);
  const [pickB, setPickB] = useState<string | null>(null);
  // Reihenfolge der gewählten Move-IDs (Index 0 = Pick 1).
  const [selectedMoveIds, setSelectedMoveIds] = useState<string[]>([]);

  // Filter/Sort State
  type ParentSort = "levelDesc" | "nameAsc" | "newest";
  const [parentTypeFilter, setParentTypeFilter] = useState<Set<string>>(new Set());
  const [parentSort, setParentSort] = useState<ParentSort>("levelDesc");
  const [eggRarityFilter, setEggRarityFilter] = useState<Set<string>>(new Set());
  const [eggTypeFilter, setEggTypeFilter] = useState<Set<string>>(new Set());
  const [eggReadyOnly, setEggReadyOnly] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["breeding"] });
    qc.invalidateQueries({ queryKey: ["eggs"] });
    qc.invalidateQueries({ queryKey: ["items"] });
    qc.invalidateQueries({ queryKey: ["my-monsters"] });
  };

  const startMut = useMutation({
    mutationFn: (v: { parentAId: string; parentBId: string; selectedMoveIds: string[] }) =>
      start({ data: v }),
    onSuccess: () => {
      invalidate();
      setPickA(null);
      setPickB(null);
      setSelectedMoveIds([]);
      toast.success(t("breeding.toastStarted"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const claimMut = useMutation({
    mutationFn: (jobId: string) => claim({ data: { jobId } }),
    onSuccess: (r) => {
      invalidate();
      toast.success(t("breeding.toastClaimed", { type: r.type }));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const cancelMut = useMutation({
    mutationFn: (jobId: string) => cancel({ data: { jobId } }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  type EggCache = { eggs?: Array<{ id: string; qty: number; [k: string]: unknown }> } | undefined;
  const hatchMut = useMutation({
    mutationFn: (id: string) => hatch({ data: { itemId: id } }),
    onMutate: async (id: string) => {
      await qc.cancelQueries({ queryKey: ["eggs"] });
      const prev = qc.getQueryData<EggCache>(["eggs"]);
      qc.setQueryData<EggCache>(["eggs"], (old) => {
        if (!old?.eggs) return old;
        const eggs = old.eggs
          .map((e) => (e.id === id ? { ...e, qty: e.qty - 1 } : e))
          .filter((e) => e.qty > 0);
        return { ...old, eggs };
      });
      return { prev };
    },
    onSuccess: (r) => {
      toast.success(t("breeding.toastHatching"), {
        duration: 5000,
      });
      const monsterId = (r as { monster?: { id?: string } })?.monster?.id;
      if (monsterId) {
        regen({ data: { monsterId } })
          .then(() => {
            qc.invalidateQueries({ queryKey: ["my-monsters"] });
            toast.success(t("breeding.toastHatchComplete"));
          })
          .catch((e: Error) => {
            qc.invalidateQueries({ queryKey: ["my-monsters"] });
            toast.error(t("breeding.toastHatchAppearanceFail", { message: e.message }));
          });
      }
    },
    onError: (e: Error, _id, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(["eggs"], ctx.prev);
      toast.error(tServerError(e));
    },
    onSettled: () => {
      invalidate();
    },
  });

  const sellMut = useMutation({
    mutationFn: (id: string) => sellFn({ data: { itemId: id } }),
    onSuccess: (r) => {
      invalidate();
      toast.success(`+${r.gold} Gold`);
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const bulkEggMut = useMutation({
    mutationFn: (maxRarity: "common" | "rare" | "epic" | "legendary") =>
      sellAllEggsFn({ data: { maxRarity } }),
    onSuccess: (r) => {
      invalidate();
      if (r.sold === 0) toast.info(t("breeding.toastNoEggs"));
      else toast.success(t("breeding.toastBulkSold", { sold: r.sold, gold: r.gold }));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const startIncubateMut = useMutation({
    mutationFn: (id: string) => startIncubate({ data: { itemId: id } }),
    onSuccess: () => {
      invalidate();
      toast.success(t("breeding.toastIncubationStarted"));
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const eligible = (data?.eligible ?? []) as Parent[];
  const jobs = (data?.jobs ?? []) as Job[];
  const incubating = jobs.filter((j) => j.status === "incubating");
  const eggs = (eggData?.eggs ?? []) as Array<{
    id: string;
    name: string;
    rarity: string;
    qty: number;
    stats: Record<string, unknown> | null;
    hatch_at: string | null;
    created_at: string;
  }>;
  const nowMs = Date.now();
  const activeIncubating = eggs.filter((e) => e.hatch_at && new Date(e.hatch_at).getTime() > nowMs).length;
  const slotsFull = activeIncubating >= incubatorCap;

  type EggSortKey = "newest" | "oldest" | "rarityDesc" | "rarityAsc" | "nameAsc" | "incubating";
  const [eggSort, setEggSort] = useState<EggSortKey>("newest");
  const sortedEggs = useMemo(() => {
    const rarityRank: Record<string, number> = {
      common: 1, rare: 2, epic: 3, legendary: 4, mythic: 5,
    };
    const arr = [...eggs];
    const ts = (s: string | null) => (s ? Date.parse(s) : 0);
    switch (eggSort) {
      case "oldest":
        arr.sort((a, b) => ts(a.created_at) - ts(b.created_at)); break;
      case "rarityDesc":
        arr.sort((a, b) => (rarityRank[b.rarity] ?? 0) - (rarityRank[a.rarity] ?? 0)
          || ts(b.created_at) - ts(a.created_at)); break;
      case "rarityAsc":
        arr.sort((a, b) => (rarityRank[a.rarity] ?? 0) - (rarityRank[b.rarity] ?? 0)
          || ts(b.created_at) - ts(a.created_at)); break;
      case "nameAsc":
        arr.sort((a, b) => a.name.localeCompare(b.name, "de")); break;
      case "incubating":
        arr.sort((a, b) => Number(Boolean(b.hatch_at)) - Number(Boolean(a.hatch_at))
          || ts(a.hatch_at) - ts(b.hatch_at)
          || ts(b.created_at) - ts(a.created_at)); break;
      case "newest":
      default:
        arr.sort((a, b) => ts(b.created_at) - ts(a.created_at)); break;
    }
    // Ready-first: fertig ausgebrütete Eier immer oben, dann Ruhende, dann laufend brütend (kürzeste Restzeit zuerst).
    const nowT = Date.now();
    const group = (e: (typeof arr)[number]) => {
      const h = ts(e.hatch_at);
      if (h > 0 && h <= nowT) return 0; // bereit
      if (h === 0) return 1; // ruht
      return 2; // brütet
    };
    arr.sort((a, b) => {
      const ga = group(a), gb = group(b);
      if (ga !== gb) return ga - gb;
      if (ga === 2) return ts(a.hatch_at) - ts(b.hatch_at);
      return 0;
    });
    return arr;
  }, [eggs, eggSort]);

  // Verfügbare Filter-Optionen (nur was im Bestand vorkommt)
  const eligibleTypes = useMemo(
    () => Array.from(new Set(eligible.map((m) => m.type))),
    [eligible],
  );
  const eggRarities = useMemo(
    () => Array.from(new Set(eggs.map((e) => e.rarity))),
    [eggs],
  );
  const eggTypes = useMemo(() => {
    const set = new Set<string>();
    for (const e of eggs) {
      const t = (e.stats as { type?: string } | null)?.type;
      if (t) set.add(t);
    }
    return Array.from(set);
  }, [eggs]);

  // Eltern: Filter + Sortierung (ausgewählte Eltern bleiben immer sichtbar)
  const filteredEligible = useMemo(() => {
    let arr = eligible;
    if (parentTypeFilter.size > 0) {
      arr = arr.filter(
        (m) => parentTypeFilter.has(m.type) || m.id === pickA || m.id === pickB,
      );
    }
    const sorted = [...arr];
    switch (parentSort) {
      case "nameAsc":
        sorted.sort((x, y) => x.name.localeCompare(y.name, "de"));
        break;
      case "newest":
        sorted.reverse();
        break;
      case "levelDesc":
      default:
        sorted.sort((x, y) => (y.level ?? 0) - (x.level ?? 0));
        break;
    }
    return sorted;
  }, [eligible, parentTypeFilter, parentSort, pickA, pickB]);

  // Eier: Filter auf der sortierten Liste
  const visibleEggs = useMemo(() => {
    const nowT = Date.now();
    return sortedEggs.filter((e) => {
      if (eggRarityFilter.size > 0 && !eggRarityFilter.has(e.rarity)) return false;
      const etype = (e.stats as { type?: string } | null)?.type;
      if (eggTypeFilter.size > 0 && (!etype || !eggTypeFilter.has(etype))) return false;
      if (eggReadyOnly) {
        const ready = e.hatch_at && new Date(e.hatch_at).getTime() <= nowT;
        if (!ready) return false;
      }
      return true;
    });
  }, [sortedEggs, eggRarityFilter, eggTypeFilter, eggReadyOnly]);

  const a = eligible.find((m) => m.id === pickA) ?? null;
  const b = eligible.find((m) => m.id === pickB) ?? null;

  // Vorschau der Ei-Rarität rein aus Eltern-Level (gleiche Formel wie Server).
  const previewRarity: EggRarity | null = useMemo(() => {
    if (!a || !b) return null;
    const avgLevel = ((a.level ?? 0) + (b.level ?? 0)) / 2;
    return rarityFromLevel(avgLevel);
  }, [a, b]);
  // Anzeige-Deckel: die Rarität erlaubt bis zu 4, beim Schlüpfen kommen aber
  // höchstens HATCH_INHERIT_LIMIT an. Angezeigt wird die kleinere Zahl.
  const previewCap = previewRarity
    ? Math.min(MOVES_PER_RARITY[previewRarity], HATCH_INHERIT_LIMIT)
    : 0;
  const previewStatBonus = previewRarity ? EGG_RARITY_STAT_BONUS[previewRarity] : 0;

  // Auswahl zurücksetzen wenn Eltern sich ändern.
  useEffect(() => {
    setSelectedMoveIds([]);
  }, [pickA, pickB]);

  // Eltern-Moves laden sobald beide gewählt sind.
  const { data: movesData, isFetching: movesLoading } = useQuery({
    queryKey: ["breeding-moves", pickA, pickB],
    queryFn: () => fetchBreedingMoves({ data: { parentAId: pickA!, parentBId: pickB! } }),
    enabled: !!pickA && !!pickB && pickA !== pickB,
  });
  const availableMoves = (movesData?.moves ?? []) as Array<{
    moveId: string;
    name: string;
    type: string;
    parentNames: string[];
  }>;

  // Auswahl über `previewCap` hinaus bleibt erlaubt (die Reihenfolge zählt),
  // wird aber ausgegraut dargestellt: Picks ab Position previewCap+1 werden
  // NICHT vererbt. 4 ist das harte Maximum des Servers (`selectedMoveIds.max(4)`).
  const toggleMove = (moveId: string) => {
    setSelectedMoveIds((prev) => {
      if (prev.includes(moveId)) return prev.filter((id) => id !== moveId);
      if (prev.length >= 4) return prev; // hartes Maximum
      return [...prev, moveId];
    });
  };

  const needsMoveSelection = availableMoves.length > 0;
  const bothPicked = !!a && !!b && a.id !== b.id;
  const canStart = bothPicked && (!needsMoveSelection || selectedMoveIds.length > 0);
  // Der Typ des Eis wird serverseitig 50/50 zwischen den Eltern gewürfelt
  // (`pickResultType`) — die Vorschau-Kachel zeigt deshalb nur eine der beiden
  // Möglichkeiten und sagt das darunter auch.
  const previewEggType: CreatureType | null =
    a && isCreatureType(a.type) ? a.type : null;

  return (
    <div className="relative min-h-screen">
      <div className="absolute inset-0 bg-grid opacity-20" aria-hidden />
      <ScreenContainer className="relative z-10">
        <PageHeader
          icon={<Egg className="h-6 w-6 shrink-0 text-primary" strokeWidth={2.5} />}
          title={t("breeding.title")}
          sub={
            <>
              {t("breeding.subtitleMain", { minLevel })}{" "}
              <InfoTip text={bi(TIP_INHERITANCE)}>
                <span className="underline decoration-dotted">
                  {bi(TXT.subtitleMoves).replace("{{n}}", String(HATCH_INHERIT_LIMIT))}
                </span>
              </InfoTip>{" "}
              {t("breeding.subtitleMovesSuffix")}
            </>
          }
        />

        {/* Zwei Wege ins Nest — beide enden bei derselben Brutzeit. */}
        <Panel tone="secondary" className="mb-5" contentClassName="p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex gap-2.5">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
              <div className="min-w-0">
                <MicroLabel>{bi(TXT.wayBreedLabel)}</MicroLabel>
                <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
                  {bi(TXT.wayBreedBody).replace("{{lvl}}", String(minLevel))}
                </p>
              </div>
            </div>
            <div className="flex gap-2.5">
              <Swords className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={2.5} />
              <div className="min-w-0">
                <MicroLabel>{bi(TXT.wayBattleLabel)}</MicroLabel>
                <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
                  {bi(TXT.wayBattleBody)}
                </p>
              </div>
            </div>
          </div>
          {/* Die beiden Schritte kommen aus denselben Keys wie die Knöpfe auf
              der Ei-Karte — so kann der Erklärtext nicht davon abweichen. */}
          <p className="mt-3 border-t-2 border-ink pt-2.5 text-[13px] leading-snug">
            {bi(TXT.nestNote)} <b>{t("breeding.startIncubate")}</b> →{" "}
            <b>{t("breeding.hatch")}</b>
          </p>
        </Panel>

        {/* ── Zucht ─────────────────────────────────────────────────────── */}
        <section className="mt-6">
          <h3 className="mb-3 text-lg">{t("breeding.parentsSectionTitle")}</h3>

          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t("breeding.loading")}</p>
          ) : eligible.length < 2 ? (
            <Panel contentClassName="p-4">
              <p className="text-sm text-muted-foreground">
                {t("breeding.noParents", { minLevel })}
              </p>
            </Panel>
          ) : (
            <div className="grid items-start gap-4 lg:grid-cols-[1.15fr_1fr]">
              {/* Linke Spalte: Filter + Elternliste */}
              <div>
                <div className="mb-3 space-y-2">
                  {eligibleTypes.length > 1 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <MicroLabel className="mr-1">{t("breeding.typeFilterLabel")}</MicroLabel>
                      <FilterChip
                        active={parentTypeFilter.size === 0}
                        onClick={() => setParentTypeFilter(new Set())}
                      >
                        {t("breeding.filterAll")}
                      </FilterChip>
                      {eligibleTypes.map((ty) => (
                        <FilterChip
                          key={ty}
                          active={parentTypeFilter.has(ty)}
                          onClick={() =>
                            setParentTypeFilter((prev) => {
                              const next = new Set(prev);
                              if (next.has(ty)) next.delete(ty);
                              else next.add(ty);
                              return next;
                            })
                          }
                        >
                          {isCreatureType(ty) ? typeLabel(ty) : ty}
                        </FilterChip>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5">
                    <MicroLabel className="mr-1">{t("breeding.sortLabel")}</MicroLabel>
                    <FilterPills<ParentSort>
                      items={[
                        ["levelDesc", t("breeding.sortLevelDesc")],
                        ["nameAsc", t("breeding.sortNameAsc")],
                        ["newest", t("breeding.sortNewest")],
                      ]}
                      value={parentSort}
                      onChange={setParentSort}
                    />
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                  {filteredEligible.map((m) => {
                    const role = m.id === pickA ? "A" : m.id === pickB ? "B" : null;
                    const mType = isCreatureType(m.type) ? m.type : null;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          if (m.id === pickA) setPickA(null);
                          else if (m.id === pickB) setPickB(null);
                          else if (!pickA) setPickA(m.id);
                          else if (!pickB) setPickB(m.id);
                          else setPickB(m.id);
                        }}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-md border-2 p-2 text-left text-foreground shadow-hard-sm",
                          "transition-[transform,box-shadow,filter] duration-100 hover:-translate-y-px hover:brightness-[1.06]",
                          "active:translate-y-[4px] active:shadow-none",
                          role ? "border-primary" : "border-ink",
                        )}
                        style={{
                          background: role
                            ? "color-mix(in oklab, var(--primary) 18%, var(--card))"
                            : "var(--card)",
                        }}
                      >
                        <PortraitFrame
                          src={m.image_url}
                          alt={m.name}
                          size={52}
                          ringColor={mType ? `var(--type-${mType})` : undefined}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-extrabold">{m.name}</span>
                          <span className="mt-1 flex flex-wrap items-center gap-1.5">
                            {mType ? <TypeBadge type={mType} /> : null}
                            <span className="text-[11px] font-extrabold tabular-nums text-muted-foreground">
                              Lv {m.level}
                            </span>
                          </span>
                        </span>
                        {role ? (
                          <span className="flex shrink-0 items-center gap-1.5">
                            <Pill style={{ background: "var(--primary)", color: "var(--ink)" }}>
                              {role}
                            </Pill>
                            <CheckCircle2 className="h-5 w-5 text-primary" strokeWidth={2.5} />
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Rechte Spalte: Vorschau + Attacken-Auswahl + Start */}
              <Panel tone="primary" grid contentClassName="p-3.5">
                <h3 className="text-[17px]">{bi(TXT.previewTitle)}</h3>

                <div className="mt-3 flex flex-wrap items-center justify-center gap-2.5">
                  {[a, b].map((p, i) =>
                    p ? (
                      <div key={p.id} className="flex flex-col items-center gap-1">
                        <PortraitFrame
                          src={p.image_url}
                          alt={p.name}
                          size={64}
                          ringColor={
                            isCreatureType(p.type) ? `var(--type-${p.type})` : undefined
                          }
                        />
                        <span className="max-w-[76px] truncate text-[11px] font-extrabold">
                          {p.name}
                        </span>
                      </div>
                    ) : (
                      <div key={`slot-${i}`} className="flex flex-col items-center gap-1">
                        <span className="grid h-16 w-16 place-items-center rounded-md border-2 border-dashed border-border-soft bg-card font-display text-xl text-muted-foreground">
                          {i === 0 ? "A" : "B"}
                        </span>
                        <span className="text-[11px] font-extrabold text-muted-foreground">—</span>
                      </div>
                    ),
                  )}
                  {bothPicked ? (
                    <>
                      <ArrowRight className="h-5 w-5 shrink-0 text-primary" strokeWidth={2.5} />
                      <EggTile type={previewEggType} size={64} />
                    </>
                  ) : null}
                </div>

                {bothPicked ? (
                  <p className="mt-2 text-center text-[11px] text-muted-foreground">
                    {bi(TXT.previewTypeRoll)}
                  </p>
                ) : (
                  <p className="mt-2 text-center text-[13px] text-muted-foreground">
                    {bi(TXT.previewPickHint)}
                  </p>
                )}

                <div className="mt-3.5 grid gap-1.5 text-xs">
                  <PreviewRow
                    label={t("breeding.previewRarityLabel")}
                    value={
                      previewRarity ? (
                        <RarityTag
                          rarity={previewRarity}
                          label={bi(RARITY_LABELS[previewRarity])}
                        />
                      ) : (
                        <span>—</span>
                      )
                    }
                  />
                  <PreviewRow
                    label={t("breeding.inheritableMovesLabel")}
                    value={previewRarity ? previewCap : "—"}
                  />
                  <PreviewRow
                    label={
                      <InfoTip text={bi(TIP_STAT_BONUS)}>
                        <span className="underline decoration-dotted">
                          {bi(TXT.statBonus)} ⓘ
                        </span>
                      </InfoTip>
                    }
                    value={previewRarity ? `+${previewStatBonus} %` : "—"}
                  />
                  {baseDurationMinutes ? (
                    <PreviewRow
                      label={
                        <InfoTip text={bi(TIP_RIPE_TIME)}>
                          <span className="underline decoration-dotted">
                            {bi(TXT.ripeTime)} ⓘ
                          </span>
                        </InfoTip>
                      }
                      value={`≤ ${baseDurationMinutes} ${bi(TXT.minutesShort)}`}
                    />
                  ) : null}
                </div>

                {/* Attacken-Auswahl — nur sinnvoll, wenn beide Eltern stehen. */}
                {bothPicked ? (
                  <div className="mt-4">
                    <MicroLabel className="mb-2">
                      {t("breeding.chooseMoves")}
                      {previewCap > 0 && (
                        <span className="ml-1.5 font-sans normal-case tracking-normal text-foreground/70">
                          {t("breeding.moveCapHint", { cap: previewCap })}
                        </span>
                      )}
                    </MicroLabel>

                    {movesLoading ? (
                      <p className="text-xs text-muted-foreground">{t("breeding.loadingMoves")}</p>
                    ) : availableMoves.length === 0 ? (
                      <p className="text-xs text-muted-foreground">{t("breeding.noParentMoves")}</p>
                    ) : (
                      <div className="grid gap-2">
                        {availableMoves.map((mv) => {
                          const pickIdx = selectedMoveIds.indexOf(mv.moveId);
                          const isPicked = pickIdx >= 0;
                          const inheritedNow = isPicked && pickIdx < previewCap;
                          return (
                            <button
                              key={mv.moveId}
                              type="button"
                              onClick={() => toggleMove(mv.moveId)}
                              className={cn(
                                "flex min-h-11 items-center gap-2 rounded-md border-2 px-2 py-1.5 text-left text-foreground shadow-hard-sm",
                                "transition-[transform,box-shadow,filter] duration-100 hover:brightness-[1.06]",
                                "active:translate-y-[4px] active:shadow-none",
                                isPicked ? "border-primary" : "border-ink",
                                isPicked && !inheritedNow && "opacity-60",
                              )}
                              style={{
                                background: isPicked
                                  ? "color-mix(in oklab, var(--primary) 18%, var(--card))"
                                  : "var(--card)",
                              }}
                            >
                              <span
                                className={cn(
                                  "grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 border-ink font-display text-[10px] tabular-nums",
                                  isPicked
                                    ? "bg-primary text-ink"
                                    : "bg-secondary text-muted-foreground",
                                )}
                              >
                                {isPicked ? pickIdx + 1 : ""}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-xs font-extrabold [overflow-wrap:anywhere] line-clamp-2">
                                  {mv.name}
                                </span>
                                <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">
                                  {isCreatureType(mv.type) ? typeLabel(mv.type) : mv.type} ·{" "}
                                  {mv.parentNames.join(" & ")}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {needsMoveSelection && selectedMoveIds.length === 0 && (
                      <p className="mt-2 text-xs font-extrabold text-primary">
                        {t("breeding.selectMoveHint")}
                      </p>
                    )}
                  </div>
                ) : null}

                <Button
                  className="mt-4 w-full"
                  disabled={!canStart || startMut.isPending}
                  onClick={() => {
                    if (canStart)
                      startMut.mutate({
                        parentAId: a!.id,
                        parentBId: b!.id,
                        selectedMoveIds,
                      });
                  }}
                >
                  <Sparkles className="h-4 w-4" strokeWidth={2.5} /> {t("breeding.startBreeding")}
                </Button>
              </Panel>
            </div>
          )}
        </section>

        {/* ── Inkubator: laufende Brut-Jobs ─────────────────────────────── */}
        {incubating.length > 0 && (
          <section className="mt-8">
            <h3 className="mb-3 flex flex-wrap items-baseline gap-2 text-lg">
              {t("breeding.incubatorTitle")}
              <InfoTip text={bi(TIP_INCUBATOR)}>
                <span className="font-sans text-[13px] font-extrabold text-muted-foreground underline decoration-dotted [text-shadow:none]">
                  {bi(TXT.cancelQuestion)} ⓘ
                </span>
              </InfoTip>
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {incubating.map((j) => {
                const remaining = new Date(j.ready_at).getTime() - Date.now();
                const isReady = remaining <= 0;
                return (
                  <JobCard
                    key={j.id}
                    job={j}
                    now={Date.now()}
                    action={
                      isReady ? (
                        /* Orange (`default`) ist die einzige Aktionsfarbe und
                           gehört dem einen CTA der Ansicht („Brut starten").
                           Dass hier etwas fertig ist, trägt bereits der
                           Panel-Ton `primary` samt „Bereit zur Abholung". */
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => claimMut.mutate(j.id)}
                          disabled={claimMut.isPending}
                        >
                          <Egg className="h-4 w-4" strokeWidth={2.5} /> {t("breeding.claimEgg")}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => cancelMut.mutate(j.id)}
                          disabled={cancelMut.isPending}
                        >
                          <X className="h-4 w-4" strokeWidth={2.5} /> {t("breeding.cancelJob")}
                        </Button>
                      )
                    }
                  />
                );
              })}
            </div>
          </section>
        )}

        {/* ── Eier-Nest ─────────────────────────────────────────────────── */}
        <section className="mt-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg">
              {t("breeding.eggNestTitle")}{" "}
              {eggs.length > 0 && (
                <span className="font-sans text-[13px] font-extrabold tabular-nums text-muted-foreground [text-shadow:none]">
                  (
                  {visibleEggs.length === eggs.length
                    ? eggs.length
                    : t("breeding.eggCountOf", { visible: visibleEggs.length, total: eggs.length })}
                  )
                </span>
              )}
            </h3>

            <div className="flex flex-wrap items-center gap-2">
              <InfoTip text={bi(TIP_SLOTS)}>
                <Pill
                  style={{
                    background: slotsFull ? "var(--secondary)" : "var(--accent)",
                    color: "var(--foreground)",
                  }}
                >
                  <Hourglass className="h-3 w-3" strokeWidth={2.5} />
                  {t("breeding.incubatorSlots", { active: activeIncubating, cap: incubatorCap })}
                </Pill>
              </InfoTip>
              <InfoTip text={bi(TIP_HATCH_TIME)}>
                <span className="text-xs font-extrabold text-muted-foreground underline decoration-dotted">
                  {t("breeding.hatchTimeHint")} ⓘ
                </span>
              </InfoTip>
              {eggs.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="secondary" disabled={bulkEggMut.isPending}>
                      <Coins className="h-4 w-4" strokeWidth={2.5} /> {t("breeding.bulkSellBtn")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("breeding.bulkSellDialogTitle")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("breeding.bulkSellDialogDesc")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button variant="secondary" className="flex-1" onClick={() => bulkEggMut.mutate("common")}>{t("breeding.bulkCommon")}</Button>
                      <Button variant="secondary" className="flex-1" onClick={() => bulkEggMut.mutate("rare")}>{t("breeding.bulkRare")}</Button>
                      <Button variant="secondary" className="flex-1" onClick={() => bulkEggMut.mutate("epic")}>{t("breeding.bulkEpic")}</Button>
                      <Button variant="secondary" className="flex-1" onClick={() => bulkEggMut.mutate("legendary")}>{t("breeding.bulkAll")}</Button>
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("breeding.close")}</AlertDialogCancel>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>

          {eggs.length > 0 && (eggRarities.length > 1 || eggTypes.length > 1) && (
            <div className="mb-3 space-y-2">
              {eggRarities.length > 1 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <MicroLabel className="mr-1">{t("breeding.rarityFilterLabel")}</MicroLabel>
                  <FilterChip active={eggRarityFilter.size === 0} onClick={() => setEggRarityFilter(new Set())}>
                    {t("breeding.filterAll")}
                  </FilterChip>
                  {eggRarities.map((r) => (
                    <FilterChip
                      key={r}
                      active={eggRarityFilter.has(r)}
                      onClick={() =>
                        setEggRarityFilter((prev) => {
                          const next = new Set(prev);
                          if (next.has(r)) next.delete(r);
                          else next.add(r);
                          return next;
                        })
                      }
                    >
                      {RARITY_LABELS[r] ? bi(RARITY_LABELS[r]) : r}
                    </FilterChip>
                  ))}
                </div>
              )}
              {eggTypes.length > 1 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <MicroLabel className="mr-1">{t("breeding.typeFilterLabel")}</MicroLabel>
                  <FilterChip active={eggTypeFilter.size === 0} onClick={() => setEggTypeFilter(new Set())}>
                    {t("breeding.filterAll")}
                  </FilterChip>
                  {eggTypes.map((ty) => (
                    <FilterChip
                      key={ty}
                      active={eggTypeFilter.has(ty)}
                      onClick={() =>
                        setEggTypeFilter((prev) => {
                          const next = new Set(prev);
                          if (next.has(ty)) next.delete(ty);
                          else next.add(ty);
                          return next;
                        })
                      }
                    >
                      {isCreatureType(ty) ? typeLabel(ty) : ty}
                    </FilterChip>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                <MicroLabel className="mr-1">{t("breeding.statusFilterLabel")}</MicroLabel>
                <FilterChip active={eggReadyOnly} onClick={() => setEggReadyOnly((v) => !v)}>
                  {t("breeding.readyOnly")}
                </FilterChip>
              </div>
            </div>
          )}

          {eggs.length > 1 && (
            <div className="mb-3 flex items-center justify-end gap-2">
              <MicroLabel>{t("breeding.sortLabel")}</MicroLabel>
              <Select value={eggSort} onValueChange={(v) => setEggSort(v as EggSortKey)}>
                <SelectTrigger className="h-9 w-[210px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t("breeding.eggSortNewest")}</SelectItem>
                  <SelectItem value="oldest">{t("breeding.eggSortOldest")}</SelectItem>
                  <SelectItem value="rarityDesc">{t("breeding.eggSortRarityDesc")}</SelectItem>
                  <SelectItem value="rarityAsc">{t("breeding.eggSortRarityAsc")}</SelectItem>
                  <SelectItem value="nameAsc">{t("breeding.eggSortNameAsc")}</SelectItem>
                  <SelectItem value="incubating">{t("breeding.eggSortIncubating")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {eggs.length === 0 ? (
            <Panel contentClassName="p-4">
              <p className="text-sm text-muted-foreground">{t("breeding.noEggs")}</p>
            </Panel>
          ) : visibleEggs.length === 0 ? (
            <Panel contentClassName="p-4">
              <p className="text-sm text-muted-foreground">{t("breeding.noEggsFilter")}</p>
            </Panel>
          ) : (
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(250px,1fr))]">
              {visibleEggs.map((e) => {
                const nestCount = visibleEggs
                  .filter((x) => x.name === e.name && x.rarity === e.rarity)
                  .reduce((sum, x) => sum + (x.qty ?? 1), 0);
                return (
                  <EggCard
                    key={e.id}
                    egg={e}
                    now={Date.now()}
                    nestCount={nestCount}
                    onHatch={() => hatchMut.mutate(e.id)}
                    onSell={() => sellMut.mutate(e.id)}
                    onStart={() => startIncubateMut.mutate(e.id)}
                    hatchPending={hatchMut.isPending && hatchMut.variables === e.id}
                    hatchBusy={hatchMut.isPending}
                    sellPending={sellMut.isPending}
                    startPending={startIncubateMut.isPending}
                    teamFull={teamFull}
                    teamCap={teamCap}
                    slotsFull={slotsFull}
                    incubatorCap={incubatorCap}
                  />
                );
              })}
            </div>
          )}
        </section>
      </ScreenContainer>
    </div>
  );
}

function JobCard({ job, now, action }: { job: Job; now: number; action: React.ReactNode }) {
  const { t } = useTranslation("game");
  const total = new Date(job.ready_at).getTime() - new Date(job.started_at).getTime();
  const elapsed = Math.min(total, now - new Date(job.started_at).getTime());
  const pct = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 100;
  const remaining = new Date(job.ready_at).getTime() - now;
  const ready = job.status === "hatched" || remaining <= 0;
  const resultType = isCreatureType(job.result_type) ? job.result_type : null;
  // Der Job speichert bis zu 4 Attacken, `hatchEgg` trägt höchstens
  // HATCH_INHERIT_LIMIT ein — angezeigt wird, was ankommt.
  const inheritedCount = Math.min(job.inherited_move_ids?.length ?? 0, HATCH_INHERIT_LIMIT);

  return (
    <Panel tone={ready ? "primary" : "default"} grid contentClassName="p-3.5">
      <div className="flex items-start gap-3">
        <EggTile type={resultType} size={52} />
        <div className="min-w-0 flex-1">
          <MicroLabel className="[overflow-wrap:anywhere] line-clamp-2">
            {job.parent_a_name} × {job.parent_b_name}
          </MicroLabel>
          <div className="ink-shadow mt-0.5 font-display text-lg leading-tight">
            {t("breeding.jobEggLabel", {
              type: resultType ? typeLabel(resultType) : job.result_type,
            })}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {job.stat_bonus_pct > 0 && (
              <InfoPill>
                +{job.stat_bonus_pct} {bi(TXT.statsSuffix)}
              </InfoPill>
            )}
            {inheritedCount > 0 && (
              <InfoPill>{t("breeding.inheritedMoves", { count: inheritedCount })}</InfoPill>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <TimeTrack pct={pct} />
      </div>
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-extrabold tabular-nums text-muted-foreground">
          <Hourglass className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          {ready ? t("breeding.jobReady") : formatRemaining(remaining)}
        </span>
        {action}
      </div>
    </Panel>
  );
}

function EggCard({
  egg,
  now,
  nestCount,
  onHatch,
  onSell,
  onStart,
  hatchPending,
  hatchBusy,
  sellPending,
  startPending,
  teamFull,
  teamCap,
  slotsFull,
  incubatorCap,
}: {
  egg: {
    id: string;
    name: string;
    rarity: string;
    qty: number;
    stats: Record<string, unknown> | null;
    hatch_at: string | null;
    created_at: string;
  };
  now: number;
  nestCount: number;
  onHatch: () => void;
  onSell: () => void;
  onStart: () => void;
  hatchPending: boolean;
  hatchBusy: boolean;
  sellPending: boolean;
  startPending: boolean;
  teamFull: boolean;
  teamCap: number;
  slotsFull: boolean;
  incubatorCap: number;
}) {
  const { t } = useTranslation("game");
  const stats = (egg.stats ?? {}) as {
    type?: string;
    stat_bonus_pct?: number;
    inherited_move_ids?: string[];
    from_breeding?: boolean;
  };
  const dormant = !egg.hatch_at;
  const hatchAt = egg.hatch_at ? new Date(egg.hatch_at).getTime() : now;
  const startedAt = new Date(egg.created_at).getTime();
  const total = Math.max(1, hatchAt - startedAt);
  const remaining = hatchAt - now;
  const ready = !dormant && remaining <= 0;
  const pct = dormant ? 0 : ready ? 100 : Math.min(100, Math.max(0, Math.round(((total - remaining) / total) * 100)));

  const type = isCreatureType(stats.type) ? stats.type : null;
  const rarity = isRarity(egg.rarity) ? egg.rarity : null;
  // Kampf-Eier tragen kein `stat_bonus_pct` — `hatchEgg` nimmt dann den
  // Raritäts-Bonus. Genau diese Regel steht hier, damit die Karte nicht „0 %"
  // suggeriert, wo der Schlüpfling +35 % bekommt.
  const statBonus = stats.stat_bonus_pct ?? (rarity ? EGG_RARITY_STAT_BONUS[rarity] : 0);
  // Wie in der JobCard: gezählt wird, was `hatchEgg` tatsächlich einträgt.
  const inheritedCount = Math.min(
    stats.inherited_move_ids?.length ?? 0,
    HATCH_INHERIT_LIMIT,
  );

  return (
    <Panel
      tone={ready ? "primary" : "default"}
      grid
      holo={rarity === "legendary"}
      contentClassName="flex h-full flex-col p-3.5"
    >
      <div className="flex items-start gap-3">
        <EggTile type={type} size={52} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-extrabold [overflow-wrap:anywhere] line-clamp-2">
            {egg.name}
          </div>
          {nestCount > 1 && (
            <div className="mt-0.5 text-[11px] font-extrabold tabular-nums text-muted-foreground">
              · {nestCount} {bi(TXT.inNest)}
            </div>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {rarity ? (
              /* `label` überschreibt die deutschen Default-Labels von
                 `RarityTag` — sonst stünde in der englischen Fassung „Selten"
                 neben der Filterpille „Rare". Sobald Badges.tsx seine Labels
                 exportiert/übersetzt, fällt der Override weg. */
              <RarityTag rarity={rarity} label={bi(RARITY_LABELS[rarity])} />
            ) : (
              <InfoPill>{egg.rarity}</InfoPill>
            )}
            {type ? (
              <TypeBadge type={type} />
            ) : (
              <InfoTip text={bi(TIP_EGG_TYPE_OPEN)}>
                <InfoPill>{bi(TXT.typeOpen)} ⓘ</InfoPill>
              </InfoTip>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {stats.from_breeding && <InfoPill>{t("breeding.fromBreeding")}</InfoPill>}
        {statBonus > 0 && (
          <InfoPill>
            +{statBonus} {bi(TXT.statsSuffix)}
          </InfoPill>
        )}
        {inheritedCount > 0 && (
          <InfoPill>{t("breeding.inheritedMoves", { count: inheritedCount })}</InfoPill>
        )}
      </div>

      {!dormant && (
        <div className="mt-3">
          <TimeTrack pct={pct} />
        </div>
      )}

      <div className="mt-2.5 flex items-center gap-1.5 text-xs font-extrabold tabular-nums">
        <Hourglass className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={2.5} />
        <span className={ready ? "text-hp-ally" : "text-muted-foreground"}>
          {dormant ? t("breeding.eggDormant") : ready ? t("breeding.eggReady") : formatRemaining(remaining)}
        </span>
      </div>

      <div className="mt-auto flex gap-2 pt-3">
        {dormant ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={onStart}
            disabled={startPending || slotsFull}
            title={slotsFull ? t("breeding.slotFullTitle", { cap: incubatorCap }) : undefined}
            className="flex-1"
          >
            <Hourglass className="h-4 w-4" strokeWidth={2.5} />
            {startPending
              ? t("breeding.startIncubating")
              : slotsFull
                ? t("breeding.slotsFull", { cap: incubatorCap })
                : t("breeding.startIncubate")}
          </Button>
        ) : (
          <Button
            size="sm"
            /* Nie orange: Ein Nest kann mehrere bereite Eier zeigen, und die
               einzige Aktionsfarbe der Ansicht gehört „Brut starten". Dass
               dieses Ei bereit ist, sagen der Panel-Ton `primary`, die Zeile
               „Bereit zum Ausbrüten" und das Knopf-Label. */
            variant="secondary"
            onClick={onHatch}
            disabled={!ready || hatchPending || hatchBusy || teamFull}
            title={teamFull ? t("breeding.teamFullTitle", { cap: teamCap }) : undefined}
            className="flex-1"
            data-tutorial-target={ready ? "hatch-button" : undefined}
          >
            <Egg className="h-4 w-4" strokeWidth={2.5} />
            {ready
              ? teamFull
                ? t("breeding.teamFull", { cap: teamCap })
                : (hatchPending || hatchBusy) ? t("breeding.hatching") : t("breeding.hatch")
              : t("breeding.incubatingCountdown", { time: formatRemaining(remaining) })}
          </Button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              disabled={sellPending}
              title={t("breeding.sellEggTitle")}
              aria-label={t("breeding.sellEggTitle")}
              /* Die Münze bleibt gelb — im Design ist Gold die einzige gelbe
                 Farbe und markiert jede Gold-Aktion. */
              className="text-gold hover:text-gold"
            >
              <Coins className="h-4 w-4" strokeWidth={2.5} />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("breeding.sellEggDialogTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("breeding.sellEggDialogDesc", { gold: sellPrice(egg.rarity, egg.qty) })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("breeding.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={onSell}>{t("breeding.sellEggConfirm")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Panel>
  );
}
